import { MessageCircle } from "lucide-react";
import { PRODUCTS, PHOTO_LABEL, whatsappUrl } from "@/lib/constants";
import { track, EVENTS } from "@/lib/analytics";
import { useEnquiry } from "@/context/EnquiryContext";
import { Reveal } from "@/components/Reveal";

export const Products = ({ heading = "Core Products", intro, showAll = true }) => {
  const { openEnquiry } = useEnquiry();
  const list = showAll ? PRODUCTS : PRODUCTS.slice(0, 4);

  return (
    <section id="products" className="py-16 sm:py-20 px-4 sm:px-6 max-w-7xl mx-auto" data-testid="products-section">
      <Reveal className="max-w-3xl mb-10 space-y-3">
        <span className="text-xs font-bold tracking-widest text-spice-green uppercase">Wholesale spice portfolio</span>
        <h2 className="text-2xl sm:text-3xl font-extrabold text-spice-ink">{heading}</h2>
        <p className="text-sm text-spice-muted">
          {intro ||
            "Supplied in wholesale lots and bulk bags. Grade and availability are confirmed before order, and rates are quoted against the current lot."}
        </p>
      </Reveal>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {list.map((product, i) => (
          <Reveal key={product.id} delay={(i % 4) * 70}>
            <article
              className="bg-white rounded-lg border border-spice-line overflow-hidden flex flex-col h-full shadow-sm hover:shadow-md hover:-translate-y-1 transition-all"
              data-testid={`product-card-${product.id}`}
            >
              <div className="aspect-[4/3] relative overflow-hidden bg-spice-sand">
                <img
                  src={product.image.url}
                  alt={product.image.alt}
                  width={product.image.width}
                  height={product.image.height}
                  loading="lazy"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
                <span className={`absolute top-3 left-3 text-[11px] ${product.tagClass} text-white px-2 py-0.5 rounded font-medium`}>
                  {product.tag}
                </span>
                <span className="absolute bottom-0 left-0 right-0 text-[10px] bg-spice-ink/70 text-white px-2 py-1" data-testid={`product-photo-label-${product.id}`}>
                  {PHOTO_LABEL}
                </span>
              </div>

              <div className="p-5 space-y-2 flex-1">
                <h3 className="text-base font-bold text-spice-ink">{product.name}</h3>
                <p className="text-xs text-spice-muted leading-relaxed">{product.description}</p>
                <div className="pt-2">
                  <span className="text-[11px] font-semibold text-spice-green block">Typical buyers / applications</span>
                  <span className="text-[11px] text-spice-subtle">{product.applications}</span>
                </div>
              </div>

              <div className="p-5 pt-0 space-y-2">
                <button
                  type="button"
                  onClick={() => {
                    track(EVENTS.productQuoteClick, { product: product.name });
                    openEnquiry({ product: product.name, source: "product-card" });
                  }}
                  data-testid={`product-quote-button-${product.id}`}
                  className="w-full inline-flex items-center justify-center py-2.5 bg-spice-green text-white text-xs font-bold rounded-[9px] hover:bg-spice-green-dark transition-colors"
                >
                  Request Quote
                </button>
                <a
                  href={whatsappUrl(product.name)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Enquire about ${product.name} on WhatsApp (opens WhatsApp in a new tab)`}
                  onClick={() => track(EVENTS.whatsappClick, { source: "product-card", product: product.name })}
                  data-testid={`product-whatsapp-button-${product.id}`}
                  className="w-full inline-flex items-center justify-center gap-1.5 py-2 border border-spice-line text-spice-green text-xs font-semibold rounded-[9px] hover:bg-spice-sand transition-colors"
                >
                  <MessageCircle className="w-3.5 h-3.5" aria-hidden="true" />
                  WhatsApp
                </a>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </section>
  );
};
